import { useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  useDeleteNotificationSetting,
  useNotificationPreview,
  useTestNotification,
  useUpdateNotificationSetting,
  type EventCatalogOut,
  type EventSettingOut,
  type PreviewOut,
} from '@/api/endpoints/notifications'
import { Button } from '@/components/ui/Button'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import styles from './TemplateEditorDialog.module.scss'
import { errorMessage } from '@/lib/errors'

interface Props {
  event: EventCatalogOut
  /** Override salvo neste escopo. Opcional: o dialog abre a partir do catálogo
   *  mesmo se o GET /settings não tiver carregado (ex.: falha de rede). */
  setting?: EventSettingOut | undefined
  channelSlug: string | null
  onClose: () => void
}

const MENTION_INHERIT = '__inherit__'
const MENTION_NONE = '__none__'

/** Traduz o override salvo de mention (null|""|"@here") pro value do Select. */
function mentionToSelect(mention: string | null): string {
  if (mention === null) return MENTION_INHERIT
  if (mention === '') return MENTION_NONE
  return mention
}

/** Traduz o value do Select de volta pro que vai no PUT. */
function selectToMention(value: string): string | null {
  if (value === MENTION_INHERIT) return null
  if (value === MENTION_NONE) return ''
  return value
}

export function TemplateEditorDialog({ event, setting, channelSlug, onClose }: Props) {
  const [title, setTitle] = useState(setting?.title_template ?? '')
  const [body, setBody] = useState(setting?.body_template ?? '')
  const [mentionSel, setMentionSel] = useState(mentionToSelect(setting?.mention ?? null))
  const [preview, setPreview] = useState<PreviewOut | null>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const update = useUpdateNotificationSetting()
  const reset = useDeleteNotificationSetting()
  const previewMut = useNotificationPreview()
  const test = useTestNotification()

  /** Insere ``{{ var }}`` na posição do cursor do textarea do corpo. */
  function insertVar(name: string) {
    const snippet = `{{ ${name} }}`
    const el = bodyRef.current
    if (!el) {
      setBody((b) => b + snippet)
      return
    }
    const start = el.selectionStart
    const end = el.selectionEnd
    const next = body.slice(0, start) + snippet + body.slice(end)
    setBody(next)
    // Reposiciona o cursor logo após o trecho inserido.
    requestAnimationFrame(() => {
      el.focus()
      const pos = start + snippet.length
      el.setSelectionRange(pos, pos)
    })
  }

  function runPreview() {
    previewMut.mutate(
      {
        event_key: event.key,
        title_template: title || null,
        body_template: body || null,
      },
      {
        onSuccess: (data) => setPreview(data),
        onError: (err) => toast.error(errorMessage(err, 'Falha no preview')),
      },
    )
  }

  function save() {
    update.mutate(
      {
        event_key: event.key,
        channel_slug: channelSlug,
        title_template: title || null,
        body_template: body || null,
        mention: selectToMention(mentionSel),
      },
      {
        onSuccess: () => {
          toast.success('Notificação atualizada')
          onClose()
        },
        onError: (err) => toast.error(errorMessage(err, 'Falha ao salvar')),
      },
    )
  }

  function resetOverride() {
    reset.mutate(
      { event_key: event.key, channel_slug: channelSlug },
      {
        onSuccess: () => {
          toast.success('Voltou ao padrão')
          onClose()
        },
        onError: (err) => toast.error(errorMessage(err, 'Falha ao resetar')),
      },
    )
  }

  function sendTest() {
    test.mutate(
      { event_key: event.key, channel_slug: channelSlug },
      {
        onSuccess: (data) => (data.sent ? toast.success(data.detail) : toast.warning(data.detail)),
        onError: (err) => toast.error(errorMessage(err, 'Falha ao testar')),
      },
    )
  }

  return (
    <DialogRoot open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`Editar: ${event.label}`} className={styles.dialog}>
        <div className={styles.body}>
          <label className={styles.label} htmlFor="notif-title">
            Título
          </label>
          <input
            id="notif-title"
            className={styles.input}
            value={title}
            placeholder={event.default_title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <label className={styles.label} htmlFor="notif-body">
            Corpo (markdown do Discord)
          </label>
          <textarea
            id="notif-body"
            ref={bodyRef}
            className={styles.textarea}
            rows={8}
            value={body}
            placeholder={event.default_body}
            onChange={(e) => setBody(e.target.value)}
          />
          <p className={styles.hint}>
            Vazio = usa o texto padrão. Clique numa variável pra inserir no corpo:
          </p>
          <div className={styles.vars}>
            {event.variables.map((v) => (
              <button
                key={v.name}
                type="button"
                className={styles.varChip}
                title={v.description}
                onClick={() => insertVar(v.name)}
              >
                {`{{ ${v.name} }}`}
              </button>
            ))}
          </div>

          <label className={styles.label} htmlFor="notif-mention">
            Menção
          </label>
          <SelectRoot value={mentionSel} onValueChange={setMentionSel}>
            <SelectTrigger id="notif-mention" className={styles.mentionSelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={MENTION_INHERIT}>Herdar (padrão do evento)</SelectItem>
              <SelectItem value={MENTION_NONE}>Sem menção</SelectItem>
              <SelectItem value="@here">@here</SelectItem>
              <SelectItem value="@everyone">@everyone</SelectItem>
            </SelectContent>
          </SelectRoot>

          <div className={styles.previewRow}>
            <Button
              type="button"
              variant="secondary"
              onClick={runPreview}
              disabled={previewMut.isPending}
            >
              {previewMut.isPending ? 'Renderizando…' : 'Pré-visualizar'}
            </Button>
          </div>

          {preview ? (
            <div className={styles.preview}>
              <strong className={styles.previewTitle}>{preview.title}</strong>
              <pre className={styles.previewBody}>{preview.body}</pre>
              {preview.errors.length > 0 ? (
                <ul className={styles.errors}>
                  {preview.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={styles.actions}>
          <Button type="button" variant="ghost" onClick={resetOverride} disabled={reset.isPending}>
            Resetar
          </Button>
          <Button type="button" variant="secondary" onClick={sendTest} disabled={test.isPending}>
            {test.isPending ? 'Enviando…' : 'Testar no Discord'}
          </Button>
          <Button type="button" onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  )
}
